import { LoadingButton } from '@mui/lab';
import { Box, Paper, Stack, Tooltip, Typography, colors } from '@mui/material';
import { t } from 'i18next';
import { ReactNode, useEffect, useState } from 'react';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { OrganizationSensorsRepository } from '../services/OrganizationSensorsRepository';

export type Sensor = {
  name: string;
  icon: ReactNode;
  description: string;
  url?: string;
  version?: string;
  docsUrl: string;
  docsAriaLabel: string;
  downloadAriaLabel: string;
};

type Props = Sensor;

export const SensorCard = ({ name, icon, version, description, url, docsUrl, docsAriaLabel: a11yDocsLabel, downloadAriaLabel: a11yDownloadLabel }: Props) => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    const repository = new OrganizationSensorsRepository();

    // The sensor url is not available
    if (!url) {
      setAvailable(false);
      setLoading(false);
      return;
    }

    // The sensor has a url, double check that it points to an existing resource
    const load = async (target: string) => {
      setLoading(true);
      setAvailable(false);
      try {
        const sensorExists = await repository.sensorPathExists(target);
        setAvailable(sensorExists);
        setLoading(false);
      } catch {
        // we will live without them
        setAvailable(false);
        setLoading(false);
      }
    };
    load(url);

    return () => {
      repository.cancelAllRequests();
    };
  }, [url]);

  const DownloadButton = (
    <LoadingButton
      loading={loading}
      disabled={loading || !available}
      variant="outlined"
      aria-label={a11yDownloadLabel}
      // Use browser navigation for external download link
      component="a"
      href={url}
      target="_blank"
    >
      {t('sensorsList.download')}
    </LoadingButton>
  );

  return (
    <Stack component={Paper} variant="outlined" direction="column" gap={2} sx={{ p: 2, height: 1 }}>
      <Stack direction="row" gap={1.5} alignItems="center">
        <Box
          aria-hidden
          sx={{
            border: '1px solid',
            borderColor: colors.grey[200],
            borderRadius: '4px',
            height: '2.5rem',
            width: '2.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Stack direction="column">
          <Typography>{name}</Typography>
          {version && <Typography variant="caption">{t('sensorsList.version', { version })}</Typography>}
        </Stack>
      </Stack>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2">{description}</Typography>
        <Interpolation
          i18nKey="sensorsList.learnMore"
          variant="caption"
          items={[{ name: 'docs', href: UrlHelper.wrapExternalDocsUrl(docsUrl), 'aria-label': a11yDocsLabel }]}
        />
      </Box>
      <Stack direction="row" gap={2}>
        {(loading || available) && DownloadButton}
        {!loading && !available && (
          <Tooltip title={t('sensorsList.unavailable.description')} tabIndex={0}>
            <span role="tooltip">{DownloadButton}</span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};
